import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl, authFetch, authHeaders, getAuthToken } from "../config";
import styles from "./FbReelsTab.module.css";

interface Scene { seconds: string; label: string; visual_prompt: string }
interface Overlay { time: string; text: string }
interface Script {
  title: string; recipe_name: string; duration: "10 seconds"; aspect_ratio: "9:16"; style: string;
  reference_image_direction: string; scene_breakdown: Scene[]; text_overlay_style: string; text_overlays: Overlay[];
  voiceover: { voice: "female soft conversational voice" | "none"; script: string }; negative_prompt: string; group_post_caption?: string;
}
interface Reel { id: number; recipe_name: string; source_type: string; script: Script | null; caption: string; group_post_caption?: string; status: string; has_video: boolean; batch_id?: string | null; reference_image_url?: string | null; source_post_url?: string | null; video_filename?: string; video_url?: string | null; facebook_account_id?: number | null; scheduled_at?: string | null; article_recipe_id?: number | null; article_image_url?: string | null; article_uploaded?: boolean; article_post_url?: string | null; article_site_id?: string | null; created_at?: string }
interface Account { id: number; name: string; facebook_brand_name: string; is_default: number }
interface Site { id: string; name: string; domain: string; hasWpApi?: boolean; isDefault?: boolean }
interface GenerateResponse { reel?: { id: number; source_type: string; script: Script; caption: string; group_post_caption?: string; status: string; has_video: boolean; batch_id?: string | null; reference_image_url?: string | null; source_post_url?: string | null }; error?: string }
type ReelStyle = "preparation" | "finished_dish_serving";
interface BulkItem { id: string; input: string; image: File | null; status: "idle" | "generating" | "done" | "error"; error?: string }
interface ScraperWebsite { id: string; name: string; domain: string }
interface WebsiteRecipe { id: number; title: string; slug: string; featured_image: string; article_content?: string; recipe_json?: string; category_name?: string; created_at?: string }

function newBulkItem(): BulkItem {
  return { id: crypto.randomUUID(), input: "", image: null, status: "idle" };
}

function buildRecentBatches(history: Reel[]) {
  const groups = new Map<string, Reel[]>();
  history.forEach(reel => {
    if (!reel.batch_id) return;
    const items = groups.get(reel.batch_id) || [];
    items.push(reel); groups.set(reel.batch_id, items);
  });

  const batches = [...groups.entries()].map(([id, reels]) => ({ id, reels, createdAt: reels[0]?.created_at || "" }));

  // Migration 0038 cannot tag scripts created before it existed. Group very recent,
  // consecutive unscheduled caption scripts so the user's current extension run is
  // still available as one bulk operation.
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const untagged = history.filter(reel => !reel.batch_id && reel.status === "script_ready" && reel.created_at && new Date(reel.created_at).getTime() >= cutoff);
  let cluster: Reel[] = [];
  const flush = () => {
    if (cluster.length >= 2) batches.push({ id: `recent-${cluster[cluster.length - 1]!.id}`, reels: [...cluster], createdAt: cluster[0]?.created_at || "" });
    cluster = [];
  };
  untagged.forEach(reel => {
    if (!cluster.length) { cluster.push(reel); return; }
    const previous = new Date(cluster[cluster.length - 1]!.created_at!).getTime();
    const current = new Date(reel.created_at!).getTime();
    if (Math.abs(previous - current) <= 15 * 60 * 1000) cluster.push(reel);
    else { flush(); cluster.push(reel); }
  });
  flush();

  return batches
    .filter(batch => batch.reels.length >= 2)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0, 8);
}

function scriptAsText(script: Script) {
  return JSON.stringify(script, null, 2);
}

function ReelScriptDetails({ script }: { script: Script }) {
  return <section className={styles.scriptCard}>
    <div className={styles.scriptHeader}><div><span className={styles.detected}>10-second video script</span><h2>{script.title}</h2><p>{script.recipe_name} · {script.duration} · {script.aspect_ratio}</p></div><button onClick={() => navigator.clipboard.writeText(scriptAsText(script))}>Copy JSON</button></div>
    <div className={styles.stylePrompt}><span>VISUAL STYLE</span>{script.style}</div>
    <div className={styles.stylePrompt}><span>REFERENCE DIRECTION</span>{script.reference_image_direction}</div>
    <div className={styles.timeline}>{script.scene_breakdown.map((scene, index) => <article key={`${scene.seconds}-${index}`}><div className={styles.time}>{scene.seconds}</div><div className={styles.sceneBody}><span>{scene.label}</span><h3>{scene.visual_prompt}</h3></div></article>)}</div>
    <div className={styles.scriptBottom}>
      <div><span className={styles.miniTitle}>TEXT OVERLAYS</span>{script.text_overlay_style && <p className={styles.overlayStyle}>{script.text_overlay_style}</p>}{script.text_overlays.map(o => <div className={styles.overlay} key={o.time}><b>{o.time}</b><q>{o.text}</q></div>)}</div>
      {script.voiceover.script && <div className={styles.vo}><div><span className={styles.miniTitle}>VOICEOVER</span><em>{script.voiceover.voice}</em></div><p>“{script.voiceover.script}”</p><button onClick={() => navigator.clipboard.writeText(script.voiceover.script)}>Copy voiceover</button></div>}
    </div>
    {script.negative_prompt && <div className={styles.negativePrompt}><div><span className={styles.miniTitle}>VIDEO NEGATIVE PROMPT</span><button onClick={() => navigator.clipboard.writeText(script.negative_prompt || "")}>Copy</button></div><p>{script.negative_prompt}</p></div>}
    <div className={styles.jsonOutput}><div><span className={styles.miniTitle}>JSON SCRIPT</span><button onClick={() => navigator.clipboard.writeText(scriptAsText(script))}>Copy JSON</button></div><pre>{scriptAsText(script)}</pre></div>
  </section>;
}

function recipeFirstComment(postUrl?: string | null) {
  return postUrl ? `Recipe | ${postUrl}` : "";
}

function defaultGroupPostCaption(recipeName: string) {
  return `${recipeName} 🍽️\n\nRich, comforting, and full of homemade flavor.\nA satisfying combination of simple everyday ingredients.\n\nEasy and family-friendly.\n\nWould you try this? Yes or no?\n\n📌 FULL RECIPE IN THE FIRST COMMENT`;
}

async function downloadRemoteImage(reelId: number, url: string, recipeName: string) {
  const safeName = recipeName.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "recipe";
  try {
    const response = await authFetch(apiUrl(`/api/fb-reels/${reelId}/article-image`));
    if (!response.ok) throw new Error("Image download failed");
    const blob = await response.blob();
    const extension = blob.type.includes("jpeg") ? "jpg" : blob.type.includes("webp") ? "webp" : "png";
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `${safeName}-article.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function bulkArticleImagePrompt(script: Script) {
  return `Create a highly realistic vertical 3:4 homemade recipe-process collage for ${script.recipe_name}. ${script.reference_image_direction}

Use a real residential kitchen, dark speckled granite countertop, ordinary cookware, bright warm kitchen light, natural smartphone photography, generous portions, and slightly imperfect homemade preparation. Create EXACTLY FOUR photographic panels separated by thin white lines: three smaller panels across the top showing authentic ingredients, hands performing the main preparation step, and the food ready to cook; then one large bottom panel showing the fully cooked dish in a close appetizing family-style serving. Keep the kitchen, cookware, ingredients, lighting, and food consistent in every panel. Show realistic moisture, browning, crumbs, sauce, melted cheese, or steam only when appropriate. No title, captions, labels, measurements, instructions, watermark, logos, branded packaging, restaurant plating, perfect symmetry, plastic-looking food, malformed hands, duplicate utensils, or duplicate ingredients.`;
}

async function createReelArticleAndImage(reel: Reel, siteId: string, useOriginalImage = false) {
  if (!reel.script) throw new Error("Reel script is missing");
  const form = new FormData();
  form.append("site_id", siteId);
  if (useOriginalImage) {
    form.append("use_reference_image", "1");
  } else {
  const imageResponse = await fetch("http://localhost:8787/generate", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt: bulkArticleImagePrompt(reel.script), size: "1200x1600", style: "photorealistic-natural" }),
  });
  const imageData = await imageResponse.json() as { url?: string; filename?: string; error?: string };
  if (!imageResponse.ok || !imageData.url) throw new Error(imageData.error || "Codex image generation failed");
  const generatedImageResponse = await fetch(`${imageData.url}?t=${Date.now()}`);
  if (!generatedImageResponse.ok) throw new Error("Could not read the generated image from the local bridge");
  form.append("image", new File([await generatedImageResponse.blob()], imageData.filename || `${reel.recipe_name}.png`, { type: "image/png" }));
  }
  const response = await authFetch(apiUrl(`/api/fb-reels/${reel.id}/create-article`), { method: "POST", body: form });
  const data = await response.json() as { success?: boolean; recipeId?: number; imageUrl?: string; error?: string };
  if (!response.ok || !data.success || !data.recipeId) throw new Error(data.error || "Article creation failed");
  return { recipeId: data.recipeId, imageUrl: data.imageUrl || null };
}

function BulkScheduleCard({ reel, accounts, sites, defaultAccountId, defaultSiteId, useOriginalImage, onUseOriginalImage, onChanged, onDeleted, onNotice }: {
  reel: Reel;
  accounts: Account[];
  sites: Site[];
  defaultAccountId: string;
  defaultSiteId: string;
  useOriginalImage: boolean;
  onUseOriginalImage: (value: boolean) => void;
  onChanged: () => Promise<void>;
  onDeleted: (reelId: number) => void;
  onNotice: (notice: { kind: "ok" | "error"; text: string }) => void;
}) {
  const [video, setVideo] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState(reel.has_video);
  const [accountId, setAccountId] = useState(reel.facebook_account_id ? String(reel.facebook_account_id) : defaultAccountId);
  const [scheduleAt, setScheduleAt] = useState("");
  const [caption, setCaption] = useState(reel.caption || "");
  const [groupPostCaption, setGroupPostCaption] = useState(reel.group_post_caption || defaultGroupPostCaption(reel.recipe_name));
  const [firstComment, setFirstComment] = useState(recipeFirstComment(reel.article_post_url || reel.source_post_url));
  const [uploading, setUploading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [captionRegenerating, setCaptionRegenerating] = useState(false);
  const [scheduleDone, setScheduleDone] = useState(reel.status === "done" || reel.status === "scheduled");
  const [collapsed, setCollapsed] = useState(reel.status === "done" || reel.status === "scheduled");
  const [siteId, setSiteId] = useState(reel.article_site_id || defaultSiteId);
  const [articleRecipeId, setArticleRecipeId] = useState<number | null>(reel.article_recipe_id || null);
  const [articleImageUrl, setArticleImageUrl] = useState<string | null>(reel.article_image_url || null);
  const [articlePostUrl, setArticlePostUrl] = useState<string | null>(reel.article_post_url || reel.source_post_url || null);
  const [articleGenerating, setArticleGenerating] = useState(false);
  const [articlePublishing, setArticlePublishing] = useState(false);
  const [articleStage, setArticleStage] = useState("");
  const [showBatchScript, setShowBatchScript] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scriptStyle, setScriptStyle] = useState<ReelStyle>("preparation");
  const [scriptRegenerating, setScriptRegenerating] = useState(false);
  useEffect(() => { if (!accountId && defaultAccountId) setAccountId(defaultAccountId); }, [accountId, defaultAccountId]);
  useEffect(() => { if (reel.facebook_account_id) setAccountId(String(reel.facebook_account_id)); }, [reel.facebook_account_id]);
  useEffect(() => { if (!siteId && defaultSiteId) setSiteId(defaultSiteId); }, [siteId, defaultSiteId]);
  useEffect(() => {
    setArticleRecipeId(reel.article_recipe_id || null);
    setArticleImageUrl(reel.article_image_url || null);
    setArticlePostUrl(reel.article_post_url || reel.source_post_url || null);
    setFirstComment(recipeFirstComment(reel.article_post_url || reel.source_post_url));
  }, [reel.article_recipe_id, reel.article_image_url, reel.article_post_url, reel.source_post_url]);
  useEffect(() => {
    const completed = reel.status === "done" || reel.status === "scheduled";
    setScheduleDone(completed);
    if (completed) setCollapsed(true);
  }, [reel.status]);
  useEffect(() => {
    setGroupPostCaption(reel.group_post_caption || defaultGroupPostCaption(reel.recipe_name));
  }, [reel.group_post_caption, reel.recipe_name]);

  const createArticle = async () => {
    if (!reel.script || !siteId || articleGenerating) return;
    setArticleGenerating(true); setArticleStage(useOriginalImage ? "Using original post image…" : "Generating image…");
    try {
      setArticleStage("Writing article…");
      const data = await createReelArticleAndImage(reel, siteId, useOriginalImage);
      setArticleRecipeId(data.recipeId); setArticleImageUrl(data.imageUrl);
      onNotice({ kind: "ok", text: `${reel.recipe_name}: article and image created.` }); await onChanged();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Article creation failed";
      onNotice({ kind: "error", text: text.includes("fetch") ? "Start the local image bridge with python scripts/image_gen_server.py, then try again." : text });
    } finally { setArticleGenerating(false); setArticleStage(""); }
  };

  const publishArticle = async () => {
    if (!articleRecipeId || !siteId || articlePublishing) return;
    setArticlePublishing(true);
    try {
      const response = await authFetch(apiUrl(`/api/recipes/${articleRecipeId}/publish`), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ siteId }),
      });
      const data = await response.json() as { success?: boolean; post_url?: string; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Website publishing failed");
      setArticlePostUrl(data.post_url || null); setFirstComment(recipeFirstComment(data.post_url));
      onNotice({ kind: "ok", text: `${reel.recipe_name}: article published to ${sites.find(site => site.id === siteId)?.name || "your website"}.` }); await onChanged();
    } catch (error) { onNotice({ kind: "error", text: error instanceof Error ? error.message : "Website publishing failed" }); }
    finally { setArticlePublishing(false); }
  };

  const uploadVideo = async () => {
    if (!video) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("video", video);
      const headers = new Headers(); const token = getAuthToken(); if (token) headers.set("Authorization", `Bearer ${token}`);
      const response = await fetch(apiUrl(`/api/fb-reels/${reel.id}/video`), { method: "POST", headers, body: form });
      const data = await response.json() as { error?: string };
      if (!response.ok) throw new Error(data.error || "Upload failed");
      setUploaded(true); onNotice({ kind: "ok", text: `${reel.recipe_name}: video uploaded.` }); await onChanged();
    } catch (error) { onNotice({ kind: "error", text: error instanceof Error ? error.message : "Upload failed" }); }
    finally { setUploading(false); }
  };

  const scheduleReel = async () => {
    if (!uploaded || !accountId || !scheduleAt || scheduleDone) return;
    setScheduling(true);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${reel.id}/schedule`), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ facebook_account_id: Number(accountId), scheduled_at: scheduleAt, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, caption, first_comment: firstComment }),
      });
      const data = await response.json() as { error?: string; details?: string };
      if (!response.ok) throw new Error([data.error, data.details].filter(Boolean).join(": "));
      setScheduleDone(true); setCollapsed(true); onNotice({ kind: "ok", text: `${reel.recipe_name}: Reel scheduled in Metricool and marked done.` }); await onChanged();
    } catch (error) { onNotice({ kind: "error", text: error instanceof Error ? error.message : "Scheduling failed" }); }
    finally { setScheduling(false); }
  };

  const regenerateCaption = async () => {
    if (captionRegenerating) return;
    setCaptionRegenerating(true);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${reel.id}/regenerate-caption`), { method: "POST" });
      const data = await response.json() as { success?: boolean; caption?: string; error?: string };
      if (!response.ok || !data.caption) throw new Error(data.error || "Caption regeneration failed");
      setCaption(data.caption);
      onNotice({ kind: "ok", text: `${reel.recipe_name}: created a fresh Facebook caption.` });
      await onChanged();
    } catch (error) {
      onNotice({ kind: "error", text: error instanceof Error ? error.message : "Caption regeneration failed" });
    } finally { setCaptionRegenerating(false); }
  };

  const regenerateScript = async () => {
    if (scriptRegenerating) return;
    setScriptRegenerating(true);
    try {
      const response = await authFetch(apiUrl("/api/fb-reels/generate"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reel_id: reel.id, reel_style: scriptStyle }),
      });
      const data = await response.json() as GenerateResponse;
      if (!response.ok || !data.reel?.script) throw new Error(data.error || "Video script regeneration failed");
      setShowBatchScript(true);
      onNotice({ kind: "ok", text: `${reel.recipe_name}: video script regenerated using ${scriptStyle === "finished_dish_serving" ? "finished dish + serving" : "step-by-step preparation"} style.` });
      await onChanged();
    } catch (error) {
      onNotice({ kind: "error", text: error instanceof Error ? error.message : "Video script regeneration failed" });
    } finally {
      setScriptRegenerating(false);
    }
  };

  const deleteReel = async () => {
    const metricoolWarning = scheduleDone ? " The scheduled Metricool post will not be removed." : "";
    if (!window.confirm(`Delete “${reel.recipe_name}” from DishSnap? This also deletes its uploaded video, reference image, and local article.${metricoolWarning}`)) return;
    setDeleting(true);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${reel.id}`), { method: "DELETE" });
      const data = await response.json() as { success?: boolean; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Delete failed");
      onDeleted(reel.id);
      onNotice({ kind: "ok", text: `${reel.recipe_name}: deleted from DishSnap.${scheduleDone ? " Its Metricool schedule was left unchanged." : ""}` });
      await onChanged();
    } catch (error) {
      onNotice({ kind: "error", text: error instanceof Error ? error.message : "Delete failed" });
    } finally { setDeleting(false); }
  };

  if (scheduleDone && collapsed) return <article className={`${styles.batchOutputCard} ${styles.completedBatchCard}`}>
    <div className={styles.batchOutputHeader}><div><span>METRICOOL DONE ✓</span><h2>{reel.recipe_name}</h2></div><div className={styles.batchHeaderActions}><b className={styles.metricoolDoneBadge}>Scheduled</b><button type="button" className={styles.deleteReelButton} onClick={deleteReel} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</button><button type="button" onClick={() => setCollapsed(false)}>Open</button></div></div>
  </article>;

  return <article className={styles.batchOutputCard}>
    <div className={styles.batchOutputHeader}><div><span>{scheduleDone ? "METRICOOL DONE ✓" : "UPLOAD & SCHEDULE"}</span><h2>{reel.recipe_name}</h2></div><div className={styles.batchHeaderActions}><select className={styles.scriptStyleSelect} value={scriptStyle} onChange={event => setScriptStyle(event.target.value as ReelStyle)} title="Choose the regenerated video style"><option value="preparation">Step-by-step preparation</option><option value="finished_dish_serving">Finished dish + serving</option></select><button type="button" onClick={() => void regenerateScript()} disabled={scriptRegenerating}>{scriptRegenerating ? "Regenerating…" : "Regenerate video script"}</button><button onClick={() => navigator.clipboard.writeText(scriptAsText(reel.script!))}>Copy JSON script</button><button onClick={() => setShowBatchScript(value => !value)}>{showBatchScript ? "Hide script" : "Show script"}</button><button type="button" className={styles.deleteReelButton} onClick={deleteReel} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</button>{scheduleDone && <button type="button" onClick={() => setCollapsed(true)}>Collapse</button>}</div></div>
    {showBatchScript && reel.script && <div className={styles.batchScriptReveal}><ReelScriptDetails script={reel.script} /></div>}
    <div className={styles.batchArticleBar}>
      <div className={styles.batchArticleImages}>
        {reel.reference_image_url && <figure><img src={reel.reference_image_url} alt={`${reel.recipe_name} original reference`} /><figcaption>Original</figcaption></figure>}
        {articleImageUrl && <figure><img src={articleImageUrl} alt={`${reel.recipe_name} generated article`} /><figcaption>Created</figcaption></figure>}
        {!reel.reference_image_url && !articleImageUrl && <div className={styles.batchArticleIcon}>▧</div>}
      </div>
      <div><strong>Recipe article + image</strong><small>{articleStage || (articlePostUrl ? "Published to website ✓" : articleRecipeId ? `Article #${articleRecipeId} ready` : "Create the article, then publish it to one of your websites")}</small></div>
      <select value={siteId} onChange={event => setSiteId(event.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select>
      <div className={styles.batchArticleActions}>
        {reel.reference_image_url && !articleRecipeId && <label className={styles.originalImageOption}><input type="checkbox" checked={useOriginalImage} onChange={event => onUseOriginalImage(event.target.checked)} /><span>Use original</span></label>}
        {articleImageUrl && <button type="button" className={styles.downloadImageButton} onClick={() => void downloadRemoteImage(reel.id, articleImageUrl, reel.recipe_name)}>Download image ↓</button>}
        {articlePostUrl
          ? <a href={articlePostUrl} target="_blank" rel="noreferrer">Open article ↗</a>
          : !articleRecipeId
            ? <button onClick={createArticle} disabled={!siteId || articleGenerating}>{articleGenerating ? "Creating…" : "Create article + image"}</button>
            : <button onClick={publishArticle} disabled={!siteId || articlePublishing}>{articlePublishing ? "Publishing…" : "Publish to website"}</button>}
      </div>
    </div>
    <div className={styles.batchOutputGrid}>
      <div className={styles.batchVideoUpload}>
        <strong>{uploaded ? "Video ready ✓" : "Upload finished Reel"}</strong>
        <small>MP4, MOV, or WebM · up to 250 MB</small>
        <label><input type="file" accept="video/*" onChange={event => setVideo(event.target.files?.[0] || null)} />{video ? video.name : "Choose video"}</label>
        {video && <button onClick={uploadVideo} disabled={uploading}>{uploading ? "Uploading…" : uploaded ? "Replace video" : "Upload video"}</button>}
      </div>
      <div className={styles.batchScheduleForm}>
        <label>Facebook Page<select value={accountId} onChange={event => setAccountId(event.target.value)}><option value="">Select Facebook account…</option>{accounts.map(account => <option key={account.id} value={account.id}>{account.name} — {account.facebook_brand_name}</option>)}</select></label>
        <label>Publish date and time<input type="datetime-local" value={scheduleAt} onChange={event => setScheduleAt(event.target.value)} /></label>
        <label className={styles.groupCaptionField}><span>Facebook caption <button type="button" onClick={regenerateCaption} disabled={captionRegenerating}>{captionRegenerating ? "Regenerating…" : "Regenerate"}</button></span><textarea value={caption} onChange={event => setCaption(event.target.value)} /></label>
        <label className={styles.groupCaptionField}><span>Facebook group caption <button type="button" onClick={() => navigator.clipboard.writeText(groupPostCaption)}>Copy</button></span><textarea value={groupPostCaption} onChange={event => setGroupPostCaption(event.target.value)} /></label>
        <label>First comment <em>Optional</em><textarea value={firstComment} onChange={event => setFirstComment(event.target.value)} /></label>
        <button onClick={scheduleReel} disabled={scheduleDone || !uploaded || !accountId || !scheduleAt || scheduling}>{scheduleDone ? "Done ✓" : scheduling ? "Sending to Metricool…" : "Schedule Facebook Reel"}</button>
      </div>
    </div>
  </article>;
}

export function FbReelsTab() {
  const [input, setInput] = useState("");
  const [recentSearch, setRecentSearch] = useState("");
  const [selectedRecentIds, setSelectedRecentIds] = useState<Set<number>>(new Set());
  const [collectingRecent, setCollectingRecent] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [websiteMode, setWebsiteMode] = useState(false);
  const [reelStyle, setReelStyle] = useState<ReelStyle>("preparation");
  const [bulkItems, setBulkItems] = useState<BulkItem[]>(() => [newBulkItem(), newBulkItem()]);
  const [batchResults, setBatchResults] = useState<Reel[]>([]);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [current, setCurrent] = useState<Reel | null>(null);
  const [history, setHistory] = useState<Reel[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [accountId, setAccountId] = useState("");
  const [articleSiteId, setArticleSiteId] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [caption, setCaption] = useState("");
  const [groupPostCaption, setGroupPostCaption] = useState("");
  const [firstComment, setFirstComment] = useState("");
  const [video, setVideo] = useState<File | null>(null);
  const [generating, setGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [deletingReel, setDeletingReel] = useState(false);
  const [duplicatingReel, setDuplicatingReel] = useState(false);
  const [captionRegenerating, setCaptionRegenerating] = useState(false);
  const [articleGenerating, setArticleGenerating] = useState(false);
  const [bulkArticleGenerating, setBulkArticleGenerating] = useState(false);
  const [bulkArticleProgress, setBulkArticleProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [originalImageChoices, setOriginalImageChoices] = useState<Record<number, boolean>>({});
  const [useOriginalSingle, setUseOriginalSingle] = useState(false);
  const [articlePublishing, setArticlePublishing] = useState(false);
  const [articleStage, setArticleStage] = useState("");
  const [showScriptDetails, setShowScriptDetails] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [tabRefreshing, setTabRefreshing] = useState(false);
  const [scraperWebsites, setScraperWebsites] = useState<ScraperWebsite[]>([]);
  const [scraperApiToken, setScraperApiToken] = useState("");
  const [sourceWebsiteId, setSourceWebsiteId] = useState("");
  const [websiteRecipes, setWebsiteRecipes] = useState<WebsiteRecipe[]>([]);
  const [websiteRecipeSearch, setWebsiteRecipeSearch] = useState("");
  const [selectedWebsiteRecipeIds, setSelectedWebsiteRecipeIds] = useState<Set<number>>(new Set());
  const [websiteRecipesLoading, setWebsiteRecipesLoading] = useState(false);
  const [websiteRecipesPage, setWebsiteRecipesPage] = useState(1);
  const [websiteRecipesHasMore, setWebsiteRecipesHasMore] = useState(false);
  const hydratedBatchResults = useMemo(() => batchResults.map(item => {
    const saved = history.find(reel => reel.id === item.id);
    return saved ? { ...item, ...saved } : item;
  }), [batchResults, history]);
  const visibleBatchResults = useMemo(
    () => hydratedBatchResults.filter(reel => reel.status !== "done" && reel.status !== "scheduled"),
    [hydratedBatchResults]
  );

  const load = useCallback(async () => {
    const [reelsResponse, accountsResponse, sitesResponse, scraperSettingsResponse] = await Promise.all([
      authFetch(apiUrl("/api/fb-reels")), authFetch(apiUrl("/api/facebook-metricool/accounts")), authFetch(apiUrl("/api/sites")), authFetch(apiUrl("/api/scraper-settings")),
    ]);
    if (reelsResponse.ok) {
      const data = await reelsResponse.json() as { reels: Reel[] };
      setHistory(data.reels || []);
    }
    if (accountsResponse.ok) {
      const data = await accountsResponse.json() as { accounts: Account[] };
      setAccounts(data.accounts || []);
      const defaultAccount = data.accounts?.find(a => a.is_default === 1) || data.accounts?.[0];
      if (defaultAccount) setAccountId(value => value || String(defaultAccount.id));
    }
    if (sitesResponse.ok) {
      const data = await sitesResponse.json() as { sites: Site[] };
      const availableSites = data.sites || [];
      setSites(availableSites);
      const defaultSite = availableSites.find(site => site.isDefault) || availableSites[0];
      if (defaultSite) setArticleSiteId(value => value || defaultSite.id);
    }
    if (scraperSettingsResponse.ok) {
      const data = await scraperSettingsResponse.json() as { settings?: { apiToken?: string | null }; websites?: ScraperWebsite[] };
      const sourceWebsites = data.websites || [];
      setScraperApiToken(data.settings?.apiToken || "");
      setScraperWebsites(sourceWebsites);
      if (sourceWebsites[0]) setSourceWebsiteId(value => value || sourceWebsites[0]!.id);
    }
  }, []);

  const refreshReelTab = async () => {
    if (tabRefreshing) return;
    setTabRefreshing(true);
    try {
      await load();
    } finally { setTabRefreshing(false); }
  };

  useEffect(() => {
    void load();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [load]);
  useEffect(() => setShowScriptDetails(false), [current?.id]);

  const updateBulkItem = (id: string, changes: Partial<BulkItem>) => {
    setBulkItems(items => items.map(item => item.id === id ? { ...item, ...changes } : item));
  };

  const fetchWebsiteRecipes = async (pageNumber = 1, append = false) => {
    const website = scraperWebsites.find(item => item.id === sourceWebsiteId);
    if (!website || !scraperApiToken || websiteRecipesLoading) return;
    setWebsiteRecipesLoading(true); setMessage(null);
    try {
      const params = new URLSearchParams({ website: website.id, domain: website.domain, page: String(pageNumber), limit: "24" });
      if (websiteRecipeSearch.trim()) params.set("search", websiteRecipeSearch.trim());
      const response = await fetch(`/api/scraper-proxy?${params.toString()}`, { headers: { Authorization: `Bearer ${scraperApiToken}` } });
      const data = await response.json() as { success?: boolean; data?: WebsiteRecipe[]; pagination?: { hasMore?: boolean }; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Could not load website recipes");
      const recipes = data.data || [];
      setWebsiteRecipes(current => append ? [...current, ...recipes.filter(recipe => !current.some(item => item.id === recipe.id))] : recipes);
      setWebsiteRecipesPage(pageNumber);
      setWebsiteRecipesHasMore(data.pagination?.hasMore ?? recipes.length === 24);
      if (!append) setSelectedWebsiteRecipeIds(new Set());
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Could not load website recipes" });
    } finally { setWebsiteRecipesLoading(false); }
  };

  const generateOne = async (sourceInput: string, image?: File | null, batchId?: string, referenceImageUrl?: string, sourcePostUrl?: string, recipeTitle?: string): Promise<Reel> => {
    const requestOptions: RequestInit = image
      ? (() => {
          const form = new FormData(); form.append("input", sourceInput);
          if (image) form.append("image", image);
          if (batchId) form.append("batch_id", batchId);
          if (referenceImageUrl) form.append("reference_image_url", referenceImageUrl);
          if (sourcePostUrl) form.append("source_post_url", sourcePostUrl);
          if (recipeTitle) form.append("recipe_title", recipeTitle);
          form.append("reel_style", reelStyle);
          return { method: "POST", body: form };
        })()
      : { method: "POST", headers: authHeaders(), body: JSON.stringify({ input: sourceInput, batch_id: batchId, reel_style: reelStyle, reference_image_url: referenceImageUrl, source_post_url: sourcePostUrl, recipe_title: recipeTitle }) };
    const response = await authFetch(apiUrl("/api/fb-reels/generate"), requestOptions);
    const data = await response.json() as GenerateResponse;
    if (!response.ok || !data.reel) throw new Error(data.error || "Generation failed");
    return {
      id: data.reel.id,
      recipe_name: data.reel.script.recipe_name,
      source_type: data.reel.source_type,
      script: data.reel.script,
      caption: data.reel.caption,
      group_post_caption: data.reel.group_post_caption || defaultGroupPostCaption(data.reel.script.recipe_name),
      status: data.reel.status,
      has_video: false,
      batch_id: data.reel.batch_id || batchId || null,
      reference_image_url: data.reel.reference_image_url || null,
      source_post_url: data.reel.source_post_url || sourcePostUrl || null,
    };
  };

  const generate = async () => {
    if (!bulkMode && !websiteMode && !input.trim()) { setMessage({ kind: "error", text: "Add a recipe name, recipe, or Reel caption first." }); return; }
    if (websiteMode && !selectedWebsiteRecipeIds.size) { setMessage({ kind: "error", text: "Select at least one website recipe first." }); return; }
    setGenerating(true); setBatchProgress(null); setMessage(null);
    try {
      if (websiteMode) {
        const website = scraperWebsites.find(item => item.id === sourceWebsiteId);
        const selected = websiteRecipes.filter(recipe => selectedWebsiteRecipeIds.has(recipe.id));
        if (!website) throw new Error("Choose a source website");
        if (selected.length > 30) throw new Error("Select no more than 30 recipes per batch");
        setBatchResults([]);
        let nextIndex = 0; let done = 0; let failed = 0;
        const created: Reel[] = [];
        const failures: string[] = [];
        const batchId = crypto.randomUUID();
        setBatchProgress({ done: 0, total: selected.length, failed: 0 });
        const worker = async () => {
          while (nextIndex < selected.length) {
            const recipe = selected[nextIndex++]!;
            try {
              const cleanDomain = website.domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
              const recipeJson = typeof recipe.recipe_json === "string" ? recipe.recipe_json : recipe.recipe_json ? JSON.stringify(recipe.recipe_json) : "";
              const sourceInput = `${recipe.title}\n\n${recipeJson || recipe.article_content || ""}`.slice(0, 16000);
              const sourceUrl = `https://${cleanDomain}/${recipe.slug.replace(/^\//, "")}/`;
              const referenceUrl = recipe.featured_image?.startsWith("http") ? recipe.featured_image : recipe.featured_image ? `https://${cleanDomain}/${recipe.featured_image.replace(/^\//, "")}` : undefined;
              created.push(await generateOne(sourceInput, null, batchId, referenceUrl, sourceUrl, recipe.title));
            } catch (error) {
              failed += 1;
              failures.push(`${recipe.title}: ${error instanceof Error ? error.message : "Generation failed"}`);
            } finally {
              done += 1; setBatchProgress({ done, total: selected.length, failed });
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(2, selected.length) }, () => worker()));
        if (!created.length) throw new Error(failures[0] || "No website recipes could be generated");
        setBatchResults(created);
        const first = created[0]!;
        setCurrent(first); setCaption(first.caption); setGroupPostCaption(first.group_post_caption || defaultGroupPostCaption(first.recipe_name)); setFirstComment(recipeFirstComment(first.source_post_url)); setVideo(null);
        setMessage({ kind: failed ? "error" : "ok", text: failed ? `Created ${created.length} of ${selected.length} website Reels. ${failed} failed.` : `Created ${created.length} Reel scripts with website images, article links, captions, and scheduling cards.` });
      } else if (!bulkMode) {
        const reel = await generateOne(input, referenceImage);
        setCurrent(reel); setCaption(reel.caption); setGroupPostCaption(reel.group_post_caption || defaultGroupPostCaption(reel.recipe_name)); setFirstComment(""); setVideo(null); setUseOriginalSingle(false);
        setMessage({ kind: "ok", text: `Detected “${reel.recipe_name}” and created the 10-second script.` });
      } else {
        setBatchResults([]);
        const items = bulkItems.filter(item => item.input.trim());
        if (items.length < 2) throw new Error("Add at least two recipes to create a batch.");
        if (items.length > 30) throw new Error("A batch can contain up to 30 recipes.");
        if (items.some(item => item.input.length > 16000)) throw new Error("A recipe input cannot exceed 16,000 characters.");

        let nextIndex = 0;
        let done = 0;
        let failed = 0;
        const created: Reel[] = [];
        const failures: string[] = [];
        const batchId = crypto.randomUUID();
        setBatchProgress({ done: 0, total: items.length, failed: 0 });
        setBulkItems(currentItems => currentItems.map(item => items.some(active => active.id === item.id) ? { ...item, status: "idle", error: undefined } : item));

        const worker = async () => {
          while (nextIndex < items.length) {
            const index = nextIndex++;
            const item = items[index]!;
            updateBulkItem(item.id, { status: "generating", error: undefined });
            try {
              created.push(await generateOne(item.input.trim(), item.image, batchId));
              updateBulkItem(item.id, { status: "done" });
            } catch (error) {
              failed += 1;
              const errorText = error instanceof Error ? error.message : "Generation failed";
              failures.push(`${item.input}: ${errorText}`);
              updateBulkItem(item.id, { status: "error", error: errorText });
            } finally {
              done += 1;
              setBatchProgress({ done, total: items.length, failed });
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(2, items.length) }, () => worker()));
        if (created[0]) {
          setCurrent(created[0]); setCaption(created[0].caption); setGroupPostCaption(created[0].group_post_caption || defaultGroupPostCaption(created[0].recipe_name)); setFirstComment(""); setVideo(null);
        }
        if (!created.length) throw new Error(failures[0] || "No scripts could be generated.");
        setBatchResults(created);
        setMessage({
          kind: failed ? "error" : "ok",
          text: failed
            ? `Created ${created.length} of ${items.length} Reels. ${failed} failed; fix or retry the marked items.`
            : `Created ${created.length} Reel scripts. Batch generation used 2 jobs at a time.`,
        });
      }
      await load();
    } catch (err) { setMessage({ kind: "error", text: err instanceof Error ? err.message : "Generation failed" }); }
    finally { setGenerating(false); }
  };

  const createAllBatchArticles = async () => {
    if (!articleSiteId || bulkArticleGenerating) return;
    const targets = visibleBatchResults.filter(reel => reel.script && !reel.article_recipe_id);
    if (!targets.length) {
      setMessage({ kind: "ok", text: "Every article and image in this bulk operation is already created." });
      return;
    }
    setBulkArticleGenerating(true); setBulkArticleProgress({ done: 0, total: targets.length, failed: 0 }); setMessage(null);
    let nextIndex = 0; let done = 0; let failed = 0;
    const worker = async () => {
      while (nextIndex < targets.length) {
        const reel = targets[nextIndex++];
        if (!reel) break;
        try {
          const data = await createReelArticleAndImage(reel, articleSiteId, Boolean(originalImageChoices[reel.id] && reel.reference_image_url));
          setBatchResults(items => items.map(item => item.id === reel.id ? { ...item, article_recipe_id: data.recipeId, article_image_url: data.imageUrl, article_site_id: articleSiteId } : item));
        } catch (error) {
          failed += 1;
          console.error(`Failed to create article for ${reel.recipe_name}`, error);
        } finally {
          done += 1; setBulkArticleProgress({ done, total: targets.length, failed });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, targets.length) }, () => worker()));
    await load();
    setBulkArticleGenerating(false);
    setMessage({ kind: failed ? "error" : "ok", text: `Created ${targets.length - failed} of ${targets.length} articles and images.${failed ? ` ${failed} failed and can be retried.` : ""}` });
  };

  const upload = async () => {
    if (!current || !video) return;
    setUploading(true); setMessage(null);
    try {
      const form = new FormData(); form.append("video", video);
      const headers = new Headers(); const token = getAuthToken(); if (token) headers.set("Authorization", `Bearer ${token}`);
      const response = await fetch(apiUrl(`/api/fb-reels/${current.id}/video`), { method: "POST", headers, body: form });
      const data = await response.json() as { error?: string; video_url?: string };
      if (!response.ok) throw new Error(data.error || "Upload failed");
      setCurrent({ ...current, has_video: true, status: "video_ready", video_filename: video.name, video_url: data.video_url || null });
      setMessage({ kind: "ok", text: "Finished video uploaded and ready for Metricool." }); await load();
    } catch (err) { setMessage({ kind: "error", text: err instanceof Error ? err.message : "Upload failed" }); }
    finally { setUploading(false); }
  };

  const regenerateFacebookCaption = async () => {
    if (!current || captionRegenerating) return;
    setCaptionRegenerating(true); setMessage(null);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${current.id}/regenerate-caption`), { method: "POST" });
      const data = await response.json() as { success?: boolean; caption?: string; error?: string };
      if (!response.ok || !data.caption) throw new Error(data.error || "Caption regeneration failed");
      setCaption(data.caption);
      setCurrent({ ...current, caption: data.caption });
      setMessage({ kind: "ok", text: "Created a fresh Facebook caption with a hook, recipe benefit, yes/no question, call to action, and 3 hashtags." });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Caption regeneration failed" });
    } finally { setCaptionRegenerating(false); }
  };

  const schedule = async () => {
    if (!current || !accountId || !scheduleAt) { setMessage({ kind: "error", text: "Choose an account and schedule date." }); return; }
    setScheduling(true); setMessage(null);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${current.id}/schedule`), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ facebook_account_id: Number(accountId), scheduled_at: scheduleAt, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, caption, first_comment: firstComment }),
      });
      const data = await response.json() as { error?: string; details?: string };
      if (!response.ok) throw new Error([data.error, data.details].filter(Boolean).join(": "));
      setCurrent({ ...current, status: "done", caption, facebook_account_id: Number(accountId), scheduled_at: scheduleAt }); setMessage({ kind: "ok", text: "Facebook Reel scheduled in Metricool and marked done." }); await load();
    } catch (err) { setMessage({ kind: "error", text: err instanceof Error ? err.message : "Scheduling failed" }); }
    finally { setScheduling(false); }
  };

  const deleteCurrentReel = async () => {
    if (!current || deletingReel) return;
    const wasScheduled = current.status === "done" || current.status === "scheduled";
    const metricoolWarning = wasScheduled ? " The scheduled Metricool post will not be removed." : "";
    if (!window.confirm(`Delete “${current.recipe_name}” from DishSnap? This also deletes its uploaded video, reference image, and local article.${metricoolWarning}`)) return;
    const deletedId = current.id;
    const deletedName = current.recipe_name;
    setDeletingReel(true); setMessage(null);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${deletedId}`), { method: "DELETE" });
      const data = await response.json() as { success?: boolean; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Delete failed");
      setCurrent(null);
      setHistory(items => items.filter(item => item.id !== deletedId));
      setBatchResults(items => items.filter(item => item.id !== deletedId));
      setMessage({ kind: "ok", text: `${deletedName}: deleted from DishSnap.${wasScheduled ? " Its Metricool schedule was left unchanged." : ""}` });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Delete failed" });
    } finally { setDeletingReel(false); }
  };

  const duplicateCurrentReel = async (copyVideo = false) => {
    if (!current || duplicatingReel) return;
    setDuplicatingReel(true); setMessage(null);
    try {
      const response = await authFetch(apiUrl(`/api/fb-reels/${current.id}/duplicate`), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ copy_video: copyVideo }),
      });
      const data = await response.json() as { success?: boolean; reel?: Reel; error?: string };
      if (!response.ok || !data.success || !data.reel) throw new Error(data.error || "Could not create a new Reel version");
      const reel = data.reel;
      setCurrent(reel);
      setHistory(items => [reel, ...items.filter(item => item.id !== reel.id)]);
      setBulkMode(false);
      setCaption(reel.caption || "");
      setGroupPostCaption(reel.group_post_caption || defaultGroupPostCaption(reel.recipe_name));
      setFirstComment(recipeFirstComment(reel.article_post_url));
      if (reel.facebook_account_id) setAccountId(String(reel.facebook_account_id));
      setVideo(null);
      setScheduleAt("");
      setUseOriginalSingle(false);
      setShowScriptDetails(false);
      setMessage({ kind: "ok", text: copyVideo
        ? `Created a new unscheduled copy of “${reel.recipe_name}” with the same video and Facebook Page. Choose a new date to schedule it again.`
        : `Created a new unscheduled version of “${reel.recipe_name}” with the same Facebook Page. Upload the new video and choose a new date.` });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Could not create a new Reel version" });
    } finally { setDuplicatingReel(false); }
  };

  const createRecipeArticle = async () => {
    if (!current?.script || articleGenerating) return;
    setArticleGenerating(true); setArticleStage(useOriginalSingle ? "Using original post image…" : "Generating article image with Codex…"); setMessage(null);
    try {
      const form = new FormData();
      if (articleSiteId) form.append("site_id", articleSiteId);
      if (useOriginalSingle) {
        form.append("use_reference_image", "1");
      } else {
      const articleImagePrompt = `Create a highly realistic vertical 3:4 homemade recipe-process collage for ${current.script.recipe_name}. ${current.script.reference_image_direction}

Match the visual feeling of a real viral Facebook home-cooking photo: an ordinary residential kitchen, dark speckled granite countertop, stove and softly blurred small plant in the background, bright warm overhead kitchen light, natural smartphone photography, generous portions, slightly imperfect preparation, realistic moisture, browning, crumbs, melted cheese, sauce, and steam where appropriate. Keep the same countertop, cookware, lighting, ingredients, and recipe visually consistent in every panel.

COMPOSITION: Create EXACTLY FOUR photographic panels with thin clean white divider lines. Use three smaller process panels across the top 34% and one large finished-food panel across the full bottom 66%.

TOP LEFT — INGREDIENTS: Show 5–8 authentic ingredients for ${current.script.recipe_name} arranged casually in clear glass bowls and simple containers on the granite counter. Ingredients must be recognizable, raw or freshly prepared, and not overly symmetrical. Use generic unbranded packaging only.

TOP CENTER — PREPARATION: Show natural human hands performing the recipe's most recognizable preparation step using ordinary home cookware. Choose the correct action automatically: mixing, whisking, seasoning, layering, pouring, coating, or assembling.

TOP RIGHT — READY TO COOK: Show the recipe assembled or actively beginning to cook in the correct skillet, pot, baking dish, sheet pan, or slow cooker. Preserve ingredient and cookware continuity.

LARGE BOTTOM PANEL — FINISHED DISH: Show a close, mouth-watering homemade beauty shot of the fully cooked ${current.script.recipe_name}. The finished food must occupy most of the panel. Include a natural fork, spoon, spatula lift, cheese pull, cut-open center, or generous served scoop only when appropriate. Make the texture and authentic doneness very clear, with rustic uneven browning and a casual family-size portion.

No recipe title, no captions, no labels, no measurements, no instructions, no watermark, no logos, no branded packaging, no restaurant plating, no luxury styling, no tiny portions, no perfect symmetry, no artificial plastic-looking food, no malformed hands, and no duplicated utensils or ingredients.`;
      const imageResponse = await fetch("http://localhost:8787/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: articleImagePrompt, size: "1200x1600", style: "photorealistic-natural" }),
      });
      const imageData = await imageResponse.json() as { url?: string; filename?: string; error?: string };
      if (!imageResponse.ok || !imageData.url) throw new Error(imageData.error || "Codex image generation failed");

      setArticleStage("Uploading image to R2 and writing recipe article…");
      const generatedImageResponse = await fetch(`${imageData.url}?t=${Date.now()}`);
      if (!generatedImageResponse.ok) throw new Error("Could not read the generated image from the local bridge");
      const imageBlob = await generatedImageResponse.blob();
      form.append("image", new File([imageBlob], imageData.filename || `${current.recipe_name}.png`, { type: "image/png" }));
      }
      const response = await authFetch(apiUrl(`/api/fb-reels/${current.id}/create-article`), { method: "POST", body: form });
      const data = await response.json() as { success?: boolean; recipeId?: number; recipeName?: string; imageUrl?: string; error?: string };
      if (!response.ok || !data.success || !data.recipeId) throw new Error(data.error || "Article creation failed");
      setCurrent({ ...current, article_recipe_id: data.recipeId, article_image_url: data.imageUrl || null, article_site_id: articleSiteId || null });
      setMessage({ kind: "ok", text: `Created the recipe JSON, full article, and Codex image for “${data.recipeName || current.recipe_name}”. You can find it in the Jobs tab recipe list.` });
      await load();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Article creation failed";
      setMessage({ kind: "error", text: text.includes("fetch") ? "Start the local image bridge with python scripts/image_gen_server.py, then try again." : text });
    } finally {
      setArticleGenerating(false); setArticleStage("");
    }
  };

  const publishRecipeArticle = async () => {
    if (!current?.article_recipe_id || !articleSiteId || articlePublishing) return;
    setArticlePublishing(true); setMessage(null);
    try {
      const response = await authFetch(apiUrl(`/api/recipes/${current.article_recipe_id}/publish`), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ siteId: articleSiteId }),
      });
      const data = await response.json() as { success?: boolean; post_url?: string; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Website publishing failed");
      setCurrent({ ...current, article_uploaded: true, article_post_url: data.post_url || null, article_site_id: articleSiteId });
      setFirstComment(recipeFirstComment(data.post_url));
      setMessage({ kind: "ok", text: `Recipe published to ${sites.find(site => site.id === articleSiteId)?.name || "your website"}.` });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Website publishing failed" });
    } finally {
      setArticlePublishing(false);
    }
  };

  const selectHistory = (reel: Reel) => { setCurrent(reel); setCaption(reel.caption || ""); setGroupPostCaption(reel.group_post_caption || defaultGroupPostCaption(reel.recipe_name)); setFirstComment(recipeFirstComment(reel.article_post_url)); setVideo(null); setScheduleAt(""); setUseOriginalSingle(false); if (reel.facebook_account_id) setAccountId(String(reel.facebook_account_id)); if (reel.article_site_id) setArticleSiteId(reel.article_site_id); setMessage(null); };
  const recentBatches = useMemo(() => buildRecentBatches(history), [history]);
  const filteredHistory = useMemo(() => {
    const term = recentSearch.trim().toLocaleLowerCase();
    if (!term) return history.slice(0, 12);
    return history.filter(reel => `${reel.recipe_name} ${reel.caption || ""} ${reel.status}`.toLocaleLowerCase().includes(term)).slice(0, 40);
  }, [history, recentSearch]);
  const openRecentBatch = (reels: Reel[]) => {
    setBulkMode(true); setBatchResults(reels);
    if (reels[0]) { setCurrent(reels[0]); setCaption(reels[0].caption || ""); setGroupPostCaption(reels[0].group_post_caption || defaultGroupPostCaption(reels[0].recipe_name)); setFirstComment(recipeFirstComment(reels[0].article_post_url)); setVideo(null); }
    setMessage({ kind: "ok", text: `Opened ${reels.length} scripts as one bulk operation. Complete the article, video, and scheduling process below.` });
  };
  const collectRecentAsBatch = async () => {
    const selected = history.filter(reel => selectedRecentIds.has(reel.id));
    if (!selected.length || collectingRecent) return;
    setCollectingRecent(true); setMessage(null);
    try {
      let nextIndex = 0;
      const prepared: Array<{ index: number; reel: Reel }> = [];
      const failures: string[] = [];
      const worker = async () => {
        while (nextIndex < selected.length) {
          const index = nextIndex++;
          const source = selected[index]!;
          const needsFreshCopy = source.status === "done" || source.status === "scheduled" || source.has_video;
          if (!needsFreshCopy) {
            prepared.push({ index, reel: source });
            continue;
          }
          try {
            const response = await authFetch(apiUrl(`/api/fb-reels/${source.id}/duplicate`), {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({ copy_video: false }),
            });
            const data = await response.json() as { success?: boolean; reel?: Reel; error?: string };
            if (!response.ok || !data.success || !data.reel) throw new Error(data.error || "Could not prepare Reel");
            prepared.push({ index, reel: data.reel });
          } catch (error) {
            failures.push(`${source.recipe_name}: ${error instanceof Error ? error.message : "Failed"}`);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(2, selected.length) }, () => worker()));
      const reels = prepared.sort((a, b) => a.index - b.index).map(item => item.reel);
      if (!reels.length) throw new Error(failures[0] || "No Reels could be prepared");
      setBulkMode(true);
      setBatchResults(reels);
      setSelectedRecentIds(new Set());
      const first = reels[0]!;
      setCurrent(first); setCaption(first.caption || ""); setGroupPostCaption(first.group_post_caption || defaultGroupPostCaption(first.recipe_name)); setFirstComment(recipeFirstComment(first.article_post_url)); setVideo(null);
      setMessage({ kind: failures.length ? "error" : "ok", text: failures.length
        ? `Prepared ${reels.length} of ${selected.length} Reels as a batch. ${failures.length} failed.`
        : `Prepared ${reels.length} selected Reels as one batch. Completed Reels were copied so you can upload new videos and schedule them again.` });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Could not prepare the selected batch" });
    } finally { setCollectingRecent(false); }
  };
  const canSchedule = Boolean(current?.has_video && accountId && scheduleAt);
  const videoPreview = useMemo(() => video ? URL.createObjectURL(video) : "", [video]);
  const referencePreview = useMemo(() => referenceImage ? URL.createObjectURL(referenceImage) : "", [referenceImage]);
  return (
    <div className={styles.page}>
      <div className={styles.pageRefreshBar}><button type="button" onClick={() => void refreshReelTab()} disabled={tabRefreshing} title="Refresh Facebook Reels data without reloading the browser">↻ {tabRefreshing ? "Refreshing…" : "Refresh"}</button></div>
      {message && <div className={`${styles.notice} ${styles[message.kind]}`}>{message.text}</div>}

      <div className={styles.workspace}>
        <section className={styles.composer}>
          <div className={styles.sectionTitle}><div><span>01</span><h2>Create Reel script</h2></div><small>{websiteMode ? "Uses the saved recipe title, JSON, article, and featured image" : "OpenAI detects the recipe automatically"}</small></div>
          <div className={styles.creationMode}>
            <button className={!bulkMode && !websiteMode ? styles.activeMode : ""} onClick={() => { setBulkMode(false); setWebsiteMode(false); }}>Single</button>
            <button className={bulkMode && !websiteMode ? styles.activeMode : ""} onClick={() => { setBulkMode(true); setWebsiteMode(false); setReferenceImage(null); }}>Bulk creation</button>
            <button className={websiteMode ? styles.activeMode : ""} onClick={() => { setWebsiteMode(true); setBulkMode(false); setReferenceImage(null); }}>Website posts</button>
            {(bulkMode || websiteMode) && <span>{websiteMode ? "Search your recipe websites, select posts, and create scheduling cards · 2 generated at a time" : "Each Reel has its own recipe input and optional reference image · 2 generated at a time"}</span>}
          </div>
          <div className={styles.reelStylePicker}>
            <div><strong>Reel style</strong><small>Used for {bulkMode || websiteMode ? "every Reel in this batch" : "this Reel"}</small></div>
            <button type="button" className={reelStyle === "preparation" ? styles.activeReelStyle : ""} onClick={() => setReelStyle("preparation")}>
              <b>Step-by-step preparation</b><span>Finished hook, then cooking steps</span>
            </button>
            <button type="button" className={reelStyle === "finished_dish_serving" ? styles.activeReelStyle : ""} onClick={() => setReelStyle("finished_dish_serving")}>
              <b>Finished dish + serving</b><span>Serve, plate, fork lift · female hook voiceover · no preparation</span>
            </button>
          </div>
          {!bulkMode && !websiteMode && <textarea value={input} onChange={e => setInput(e.target.value)} placeholder={'Try “Lemon angel food cake bars”\n\n—or paste the full recipe or an existing Reel caption.'} />}
          {!bulkMode && !websiteMode && <div className={styles.referenceUpload}>
            {referencePreview ? <img src={referencePreview} alt="Optional finished dish reference" /> : <div className={styles.referencePlaceholder}>＋</div>}
            <div><strong>Finished-dish reference image <span>Optional</span></strong><p>Add a photo when you want the scenes, plating, colors, textures, and final reveal to match it.</p></div>
            <label><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={e => setReferenceImage(e.target.files?.[0] || null)} />{referenceImage ? "Replace image" : "Choose image"}</label>
            {referenceImage && <button type="button" onClick={() => setReferenceImage(null)}>Remove</button>}
          </div>}
          {websiteMode && <div className={styles.websiteRecipePicker}>
            <div className={styles.websiteRecipeToolbar}>
              <select value={sourceWebsiteId} onChange={event => { setSourceWebsiteId(event.target.value); setWebsiteRecipes([]); setSelectedWebsiteRecipeIds(new Set()); }}><option value="">Select source website…</option>{scraperWebsites.map(website => <option key={website.id} value={website.id}>{website.name} — {website.domain}</option>)}</select>
              <input type="search" value={websiteRecipeSearch} onChange={event => setWebsiteRecipeSearch(event.target.value)} onKeyDown={event => { if (event.key === "Enter") void fetchWebsiteRecipes(1); }} placeholder="Search website posts…" />
              <button type="button" onClick={() => void fetchWebsiteRecipes(1)} disabled={!sourceWebsiteId || !scraperApiToken || websiteRecipesLoading}>{websiteRecipesLoading ? "Loading…" : "Search posts"}</button>
            </div>
            {!scraperApiToken && <p className={styles.websiteRecipeHint}>Add the website API token in Scraper settings first.</p>}
            {websiteRecipes.length > 0 && <div className={styles.websiteRecipeSelectionBar}><button type="button" onClick={() => setSelectedWebsiteRecipeIds(current => current.size === websiteRecipes.length ? new Set() : new Set(websiteRecipes.map(recipe => recipe.id)))}>{selectedWebsiteRecipeIds.size === websiteRecipes.length ? "Clear all" : "Select all loaded"}</button><span>{selectedWebsiteRecipeIds.size} selected</span></div>}
            <div className={styles.websiteRecipeGrid}>{websiteRecipes.map(recipe => <label className={selectedWebsiteRecipeIds.has(recipe.id) ? styles.websiteRecipeSelected : ""} key={recipe.id}>
              <input type="checkbox" checked={selectedWebsiteRecipeIds.has(recipe.id)} onChange={() => setSelectedWebsiteRecipeIds(current => { const next = new Set(current); next.has(recipe.id) ? next.delete(recipe.id) : next.add(recipe.id); return next; })} />
              {recipe.featured_image ? <img src={recipe.featured_image} alt="" /> : <div className={styles.websiteRecipeImageFallback}>No image</div>}
              <div><strong>{recipe.title}</strong><small>{recipe.category_name || "Recipe post"}</small></div>
            </label>)}</div>
            {websiteRecipesHasMore && <button type="button" className={styles.websiteLoadMore} onClick={() => void fetchWebsiteRecipes(websiteRecipesPage + 1, true)} disabled={websiteRecipesLoading}>{websiteRecipesLoading ? "Loading…" : "Load more posts"}</button>}
          </div>}
          {bulkMode && <div className={styles.bulkItems}>
            {bulkItems.map((item, index) => <div className={`${styles.bulkItem} ${item.status === "error" ? styles.bulkItemError : item.status === "done" ? styles.bulkItemDone : ""}`} key={item.id}>
              <div className={styles.bulkItemHeader}>
                <div><span>REEL {String(index + 1).padStart(2, "0")}</span>{item.status !== "idle" && <b>{item.status === "generating" ? "Generating…" : item.status === "done" ? "Created ✓" : "Failed"}</b>}</div>
                {bulkItems.length > 2 && <button type="button" disabled={generating} onClick={() => setBulkItems(items => items.filter(candidate => candidate.id !== item.id))}>Remove</button>}
              </div>
              <textarea value={item.input} disabled={generating} onChange={e => updateBulkItem(item.id, { input: e.target.value, status: "idle", error: undefined })} placeholder="Recipe name, full recipe, or existing Reel caption" />
              <div className={styles.bulkImageField}>
                <div className={styles.bulkImageIcon}>{item.image ? "✓" : "＋"}</div>
                <div><strong>Reference image <span>Optional</span></strong><small>{item.image ? item.image.name : "Add the finished dish for a more accurate Reel script"}</small></div>
                <label><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={generating} onChange={e => updateBulkItem(item.id, { image: e.target.files?.[0] || null, status: "idle", error: undefined })} />{item.image ? "Replace" : "Choose image"}</label>
                {item.image && <button type="button" disabled={generating} onClick={() => updateBulkItem(item.id, { image: null })}>Clear</button>}
              </div>
              {item.error && <p className={styles.bulkError}>{item.error}</p>}
            </div>)}
            <button type="button" className={styles.addBulkItem} disabled={generating || bulkItems.length >= 30} onClick={() => setBulkItems(items => [...items, newBulkItem()])}>＋ Add another Reel</button>
          </div>}
          {batchProgress && generating && <div className={styles.batchProgress}><div style={{ width: `${Math.round((batchProgress.done / batchProgress.total) * 100)}%` }} /><span>{batchProgress.done} / {batchProgress.total} complete{batchProgress.failed ? ` · ${batchProgress.failed} failed` : ""}</span></div>}
          <div className={styles.composerFooter}><span>{websiteMode ? `${selectedWebsiteRecipeIds.size} website posts selected · maximum 30` : bulkMode ? `${bulkItems.filter(item => item.input.trim()).length} Reels ready · maximum 30` : `${input.length.toLocaleString()} / 16,000`}</span><button onClick={generate} disabled={generating}>{generating ? (bulkMode || websiteMode ? `Creating batch ${batchProgress?.done || 0}/${batchProgress?.total || "…"}` : "Directing your Reel…") : websiteMode ? "Generate selected website Reels →" : bulkMode ? "Generate all Reels →" : "Generate 10-second script →"}</button></div>
        </section>

        <aside className={styles.history}>
          {recentBatches.length > 0 && <>
            <div className={styles.sectionTitle}><div><span>RECENT</span><h2>Bulk operations</h2></div></div>
            <div className={styles.recentBulkList}>{recentBatches.map((batch, index) => {
              const completed = batch.reels.every(reel => reel.status === "done" || reel.status === "scheduled");
              return <button key={batch.id} className={completed ? styles.completedBulk : ""} onClick={() => openRecentBatch(batch.reels)}>
              <div><strong>Bulk {String(index + 1).padStart(2, "0")}</strong><b>{completed ? "Done ✓" : `${batch.reels.length} Reels`}</b></div>
              <span>{batch.createdAt ? new Date(batch.createdAt).toLocaleString() : "Recent batch"}</span>
              <small>{batch.reels.slice(0, 2).map(reel => reel.recipe_name).join(" · ")}{batch.reels.length > 2 ? ` +${batch.reels.length - 2}` : ""}</small>
            </button>})}</div>
            <div className={styles.historyDivider} />
          </>}
          <div className={styles.sectionTitle}><div><span>RECENT</span><h2>Scripts</h2></div></div>
          <div className={styles.historySearch}><span>⌕</span><input type="search" value={recentSearch} onChange={event => setRecentSearch(event.target.value)} placeholder="Search Upload & schedule…" />{recentSearch && <button type="button" onClick={() => setRecentSearch("")} aria-label="Clear search">×</button>}</div>
          {filteredHistory.length > 0 && <div className={styles.recentSelectionBar}><button type="button" onClick={() => setSelectedRecentIds(ids => { const next = new Set(ids); const allSelected = filteredHistory.every(reel => next.has(reel.id)); filteredHistory.forEach(reel => allSelected ? next.delete(reel.id) : next.add(reel.id)); return next; })}>{filteredHistory.every(reel => selectedRecentIds.has(reel.id)) ? "Clear results" : "Select results"}</button><span>{selectedRecentIds.size} selected</span><button type="button" className={styles.prepareBatchButton} onClick={() => void collectRecentAsBatch()} disabled={!selectedRecentIds.size || collectingRecent}>{collectingRecent ? "Preparing…" : "Work as batch"}</button></div>}
          <div className={styles.historyList}>{filteredHistory.map(reel => <div className={`${styles.historyResultRow} ${selectedRecentIds.has(reel.id) ? styles.historyResultChecked : ""}`} key={reel.id}><input type="checkbox" checked={selectedRecentIds.has(reel.id)} onChange={() => setSelectedRecentIds(ids => { const next = new Set(ids); next.has(reel.id) ? next.delete(reel.id) : next.add(reel.id); return next; })} aria-label={`Select ${reel.recipe_name}`} /><button type="button" className={current?.id === reel.id ? styles.selected : ""} onClick={() => selectHistory(reel)}><strong>{reel.recipe_name}</strong><span>{reel.status.replace("_", " ")}</span></button></div>)}{!filteredHistory.length && <p>{history.length ? "No matching Reels." : "No scripts created yet."}</p>}</div>
        </aside>
      </div>

      {(bulkMode || websiteMode) && hydratedBatchResults.length > 0 && <>
        {!websiteMode && visibleBatchResults.length > 0 && <section className={styles.bulkArticleToolbar}>
          <div><strong>Create every article + image</strong><small>Runs up to three Codex image and article jobs in parallel.</small></div>
          <select value={articleSiteId} onChange={event => setArticleSiteId(event.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select>
          <button onClick={createAllBatchArticles} disabled={!articleSiteId || bulkArticleGenerating || visibleBatchResults.every(reel => reel.article_recipe_id)}>{bulkArticleGenerating ? `Creating ${bulkArticleProgress?.done || 0}/${bulkArticleProgress?.total || visibleBatchResults.length}…` : "Create all articles + images"}</button>
          {bulkArticleProgress && <div className={styles.bulkArticleProgress}><i style={{ width: `${Math.round((bulkArticleProgress.done / bulkArticleProgress.total) * 100)}%` }} /><span>{bulkArticleProgress.done}/{bulkArticleProgress.total}{bulkArticleProgress.failed ? ` · ${bulkArticleProgress.failed} failed` : ""}</span></div>}
        </section>}
        <section className={styles.batchOutputs}>
          {hydratedBatchResults.map(reel => <BulkScheduleCard key={reel.id} reel={reel} accounts={accounts} sites={sites} defaultAccountId={accountId} defaultSiteId={articleSiteId} useOriginalImage={Boolean(originalImageChoices[reel.id])} onUseOriginalImage={value => setOriginalImageChoices(choices => ({ ...choices, [reel.id]: value }))} onChanged={load} onDeleted={reelId => { setBatchResults(items => items.filter(item => item.id !== reelId)); setHistory(items => items.filter(item => item.id !== reelId)); if (current?.id === reelId) setCurrent(null); }} onNotice={setMessage} />)}
        </section>
      </>}

      {!bulkMode && current?.script && !Array.isArray(current.script.scene_breakdown) && (
        <div className={`${styles.notice} ${styles.error}`}>This is an older script format. Enter the recipe again and generate a new script to get the exact JSON structure.</div>
      )}

      {!bulkMode && current?.script && Array.isArray(current.script.scene_breakdown) && (
        <>
          <section className={styles.scriptRevealBar}><div><span>VIDEO SCRIPT</span><strong>{current.script.title}</strong><small>Hidden by default to keep the workspace compact.</small></div><button type="button" onClick={() => setShowScriptDetails(value => !value)}>{showScriptDetails ? "Hide script details" : "Show script details"}</button></section>
          {showScriptDetails && <ReelScriptDetails script={current.script} />}

          <section className={styles.articleCreator}>
            <div className={styles.articleCreatorText}>
              <span>RECIPE CONTENT PIPELINE</span>
              <h2>Recipe article + featured image</h2>
              <p>Use the detected recipe to create recipe JSON and the full SEO article with OpenAI. A clean article image is generated with your local Codex Image Gen bridge and uploaded to R2.</p>
              {articleStage && <strong>{articleStage}</strong>}
            </div>
            {current.article_image_url && <img src={current.article_image_url} alt={`${current.recipe_name} article`} />}
            <div className={styles.articleActions}>
              <label>Publish website<select value={articleSiteId} onChange={e => setArticleSiteId(e.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select></label>
              {current.reference_image_url && !current.article_recipe_id && <label className={styles.originalImageOption}><input type="checkbox" checked={useOriginalSingle} onChange={event => setUseOriginalSingle(event.target.checked)} /><span><b>Use original post image</b><small>Skip AI image generation for this article.</small></span></label>}
              {!sites.length && <small>Add a website in Settings first.</small>}
              {current.article_recipe_id ? <div className={styles.articleReady}><b>{current.article_uploaded ? "Published ✓" : "Article ready ✓"}</b><span>Recipe #{current.article_recipe_id}</span><small>{current.article_uploaded ? "Live on your website" : "Choose a website and publish"}</small></div> : null}
              {!current.article_recipe_id
                ? <button onClick={createRecipeArticle} disabled={articleGenerating || !articleSiteId}>{articleGenerating ? "Creating article and image…" : "Create article + image →"}</button>
                : current.article_uploaded
                  ? current.article_post_url && <a href={current.article_post_url} target="_blank" rel="noreferrer">Open published recipe ↗</a>
                  : <button onClick={publishRecipeArticle} disabled={articlePublishing || !articleSiteId}>{articlePublishing ? "Publishing…" : "Publish to website →"}</button>}
            </div>
          </section>

          <section className={styles.publish}>
            <div className={styles.sectionTitle}><div><span>02</span><h2>Upload & schedule</h2></div><div className={styles.publishHeaderActions}><small>Create the video manually, then return here.</small><button type="button" onClick={() => navigator.clipboard.writeText(scriptAsText(current.script!))}>Copy JSON script</button>{(current.status === "done" || current.status === "scheduled" || current.has_video) && <button type="button" className={styles.reuseReelButton} onClick={() => void duplicateCurrentReel(false)} disabled={duplicatingReel}>{duplicatingReel ? "Creating…" : "Reuse with new video"}</button>}<button type="button" className={styles.deleteReelButton} onClick={deleteCurrentReel} disabled={deletingReel}>{deletingReel ? "Deleting…" : "Delete Reel"}</button></div></div>
            <div className={styles.publishGrid}>
              <div className={styles.uploadBox}>
                {videoPreview ? <video src={videoPreview} controls /> : <div className={styles.uploadIcon}>▶</div>}
                <h3>{current.has_video ? "Video ready" : "Upload finished Reel"}</h3><p>MP4, MOV, or WebM · up to 250 MB</p>
                {current.video_url && <div className={styles.videoUrl}><a href={current.video_url} target="_blank" rel="noreferrer">Open public video URL</a><button type="button" onClick={() => navigator.clipboard.writeText(current.video_url || "")}>Copy URL</button></div>}
                <label><input type="file" accept="video/*" onChange={e => setVideo(e.target.files?.[0] || null)} />Choose video</label>
                {video && <button onClick={upload} disabled={uploading}>{uploading ? "Uploading…" : current.has_video ? "Replace uploaded video" : "Upload video"}</button>}
              </div>
              <div className={styles.scheduleForm}>
                <label>Facebook Page<select value={accountId} onChange={e => setAccountId(e.target.value)}><option value="">Select Facebook account…</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {a.facebook_brand_name}</option>)}</select></label>
                {!accounts.length && <p className={styles.settingsHint}>Add a Metricool Facebook account in Settings first.</p>}
                <label>Publish date and time<input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} /></label>
                <label className={styles.groupCaptionField}><span>Facebook caption <button type="button" onClick={regenerateFacebookCaption} disabled={captionRegenerating}>{captionRegenerating ? "Regenerating…" : "Regenerate"}</button></span><textarea value={caption} onChange={e => setCaption(e.target.value)} /></label>
                <label className={styles.groupCaptionField}><span>Facebook group caption <button type="button" onClick={() => navigator.clipboard.writeText(groupPostCaption)}>Copy</button></span><textarea value={groupPostCaption} onChange={e => setGroupPostCaption(e.target.value)} /></label>
                <label><span>First comment <em className={styles.optionalLabel}>Optional</em></span><textarea value={firstComment} onChange={e => setFirstComment(e.target.value)} placeholder="This comment will be published automatically after the Reel." /></label>
                <div className={styles.scheduleActions}>
                  <button className={styles.scheduleButton} onClick={schedule} disabled={current.status === "done" || current.status === "scheduled" || !canSchedule || scheduling}>{current.status === "done" || current.status === "scheduled" ? "Done ✓" : scheduling ? "Sending to Metricool…" : "Schedule Facebook Reel"}</button>
                  {(current.status === "done" || current.status === "scheduled") && <button type="button" className={styles.rescheduleButton} onClick={() => void duplicateCurrentReel(true)} disabled={duplicatingReel}>{duplicatingReel ? "Preparing…" : "Schedule again"}</button>}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
